import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { inventoryEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Input type for enabling online sales
 */
export interface EnableOnlineSalesInput {
	servicesProviderId: string;
	inventoryIds: string[];
}

/**
 * Response type for enable online sales operation
 */
export interface EnableOnlineSalesResponse {
	enabled: number;
	total: number;
}

/**
 * API function to enable online sales for inventory items
 */
export const enableOnlineSales = async ({
	servicesProviderId,
	inventoryIds,
}: EnableOnlineSalesInput): Promise<EnableOnlineSalesResponse> => {
	const response = await apiClient.post<EnableOnlineSalesResponse>(
		inventoryEndpoints.enableOnlineSales(servicesProviderId),
		{ inventoryIds },
	);
	return response.data;
};

/**
 * Hook for enabling online sales for inventory items
 */
export const useEnableOnlineSalesMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: enableOnlineSales,
		onSuccess: () => {
			// Invalidate the inventory list and detail queries to ensure they get fresh data
			queryClient.invalidateQueries({
				queryKey: queries.inventory.list._def,
			});
			queryClient.invalidateQueries({
				queryKey: queries.inventory.detail._def,
			});
		},
	});
};

/**
 * Hook for enabling online sales with services provider context
 */
export const useEnableOnlineSales = () => {
	const mutation = useEnableOnlineSalesMutation();
	const currentServicesProviderId = useCurrentServicesProviderId();

	const handleEnableOnlineSales = async (inventoryIds: string[]) => {
		if (!currentServicesProviderId) {
			throw new Error('No services provider ID available');
		}

		return mutation.mutateAsync({
			servicesProviderId: currentServicesProviderId,
			inventoryIds,
		});
	};

	return {
		enableOnlineSales: handleEnableOnlineSales,
		isEnabling: mutation.isPending,
	};
};
